import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { getMeetingTranslations } from '@/constants/meeting.translations';
import { useMeetings } from '@/hooks/useMeetings';
import { collection, query, where, getDocs, doc, getDoc, limit } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { DealMeeting, MeetingStatus, TranscriptionStatus } from '@/types/meeting.types';
import TranscriptionViewer from '@/components/meetings/TranscriptionViewer';
import CreateMeetingDialog from '@/components/meetings/CreateMeetingDialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { 
  Video, 
  Search, 
  Calendar, 
  Clock, 
  Users, 
  FileText, 
  Plus,
  ArrowLeft,
  ExternalLink,
  Filter
} from 'lucide-react';
import { format } from 'date-fns';
import { fr, enUS } from 'date-fns/locale';

interface MeetingWithTranscription extends DealMeeting {
  transcriptionStatus?: TranscriptionStatus;
  dealName?: string;
}

const MeetingHistoryPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { language } = useLanguage();
  const t = getMeetingTranslations(language);
  const dateLocale = language === 'fr' ? fr : enUS;

  const {
    meetings,
    transcription,
    loadMeetings,
    loadTranscription,
    generateSummary,
    isLoading,
  } = useMeetings(user?.id || '');

  const [meetingsWithDetails, setMeetingsWithDetails] = useState<MeetingWithTranscription[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedMeeting, setSelectedMeeting] = useState<MeetingWithTranscription | null>(null);
  const [showTranscription, setShowTranscription] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [isGeneratingSummary, setIsGeneratingSummary] = useState(false);

  // Load meetings on mount
  useEffect(() => {
    loadMeetings();
  }, [loadMeetings]);

  // Enrich meetings with transcription status and deal names
  useEffect(() => {
    const enrichMeetings = async () => {
      if (meetings.length === 0) {
        setMeetingsWithDetails([]);
        return;
      }

      const enriched: MeetingWithTranscription[] = [];

      for (const meeting of meetings) {
        // Get transcription status
        const transcSnap = await getDocs(
query(collection(db, 'meetingTranscriptions'), where('meeting_id', '==', meeting.id), limit(1))
);
const transcriptionStatus = !transcSnap.empty
? transcSnap.docs[0].data().status as TranscriptionStatus
: undefined;
 
// // Deal name
let dealName: string | undefined;
if (meeting.dealId) {
const dealSnap = await getDoc(doc(db, 'deals', meeting.dealId));
if (dealSnap.exists()) {
dealName = dealSnap.data().businessName || dealSnap.data().business_name;
}
}
 
enriched.push({ ...meeting, transcriptionStatus, dealName });
      }

      setMeetingsWithDetails(enriched);
    };

    enrichMeetings();
  }, [meetings]);

  // Filter meetings
  const filteredMeetings = meetingsWithDetails.filter(meeting => {
    const matchesSearch = 
      meeting.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      meeting.dealName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      meeting.roomName.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || meeting.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const handleViewTranscription = async (meeting: MeetingWithTranscription) => {
    setSelectedMeeting(meeting);
    await loadTranscription(meeting.id);
    setShowTranscription(true);
  };

  const handleGenerateSummary = async () => {
    if (selectedMeeting) {
      setIsGeneratingSummary(true);
      await generateSummary(selectedMeeting.id);
      await loadTranscription(selectedMeeting.id);
      setIsGeneratingSummary(false);
    }
  };

  const handleJoinMeeting = (meeting: MeetingWithTranscription) => {
    navigate(`/meetings/${meeting.id}`);
  };

  const getStatusBadge = (status: MeetingStatus) => {
    const variants: Record<MeetingStatus, 'default' | 'secondary' | 'destructive' | 'outline'> = {
      scheduled: 'outline',
      active: 'default',
      ended: 'secondary',
      cancelled: 'destructive',
    };
    return <Badge variant={variants[status]}>{t[status]}</Badge>;
  };

  const getTranscriptionBadge = (status?: TranscriptionStatus) => {
    if (!status) return null;
    const variants: Record<TranscriptionStatus, 'default' | 'secondary' | 'destructive' | 'outline'> = {
      pending: 'outline',
      processing: 'secondary',
      completed: 'default',
      failed: 'destructive',
    };
    return <Badge variant={variants[status]} className="ml-2">{t[status]}</Badge>;
  };

  const formatDuration = (start?: Date, end?: Date): string => {
    if (!start || !end) return '—';
    const diff = end.getTime() - start.getTime();
    const mins = Math.floor(diff / 60000);
    const hours = Math.floor(mins / 60);
    const remainingMins = mins % 60;
    
    if (hours > 0) {
      return `${hours}h ${remainingMins}min`;
    }
    return `${mins}min`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-100 to-slate-200 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-[#1D314D]">{t.meetingHistory}</h1>
              <p className="text-sm text-muted-foreground">
                {filteredMeetings.length} {language === 'fr' ? 'réunion(s)' : 'meeting(s)'}
              </p>
            </div>
          </div>
          <Button 
            onClick={() => setShowCreateDialog(true)}
            className="bg-[#CEAB65] hover:bg-[#CEAB65]/90"
          >
            <Plus className="h-4 w-4 mr-2" />
            {t.createMeeting}
          </Button>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={t.searchTranscription}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-[180px]">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{language === 'fr' ? 'Tous les statuts' : 'All statuses'}</SelectItem>
                  <SelectItem value="scheduled">{t.scheduled}</SelectItem>
                  <SelectItem value="active">{t.active}</SelectItem>
                  <SelectItem value="ended">{t.ended}</SelectItem>
                  <SelectItem value="cancelled">{t.cancelled}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Meetings List */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1D314D]"></div>
          </div>
        ) : filteredMeetings.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Video className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              <p className="text-muted-foreground">
                {language === 'fr' ? 'Aucune réunion trouvée' : 'No meetings found'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {filteredMeetings.map((meeting) => (
              <Card key={meeting.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    {/* Meeting Info */}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-semibold text-[#1D314D]">{meeting.title}</h3>
                        {getStatusBadge(meeting.status)}
                        {getTranscriptionBadge(meeting.transcriptionStatus)}
                      </div>
                      
                      <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                        {meeting.dealName && (
                          <span className="flex items-center gap-1">
                            <FileText className="h-3.5 w-3.5" />
                            {meeting.dealName}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3.5 w-3.5" />
                          {format(meeting.createdAt, 'PPP', { locale: dateLocale })}
                        </span>
                        {meeting.startedAt && (
                          <span className="flex items-center gap-1">
                            <Clock className="h-3.5 w-3.5" />
                            {formatDuration(meeting.startedAt, meeting.endedAt)}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2">
                      {meeting.status === 'active' && (
                        <Button 
                          onClick={() => handleJoinMeeting(meeting)}
                          className="bg-[#CEAB65] hover:bg-[#CEAB65]/90"
                        >
                          <Video className="h-4 w-4 mr-2" />
                          {t.joinMeeting}
                        </Button>
                      )}
                      
                      {meeting.transcriptionStatus === 'completed' && (
                        <Button 
                          variant="outline"
                          onClick={() => handleViewTranscription(meeting)}
                        >
                          <FileText className="h-4 w-4 mr-2" />
                          {t.viewTranscription}
                        </Button>
                      )}
                      
                      {meeting.status === 'scheduled' && (
                        <Button 
                          onClick={() => handleJoinMeeting(meeting)}
                          variant="outline"
                        >
                          <ExternalLink className="h-4 w-4 mr-2" />
                          {t.startMeeting}
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Transcription Dialog */}
        <Dialog open={showTranscription} onOpenChange={setShowTranscription}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{selectedMeeting?.title} - {t.transcription}</DialogTitle>
            </DialogHeader>
            {transcription && (
              <TranscriptionViewer 
                transcription={transcription}
                onGenerateSummary={handleGenerateSummary}
                isGeneratingSummary={isGeneratingSummary}
              />
            )}
          </DialogContent>
        </Dialog>

        {/* Create Meeting Dialog */}
        <CreateMeetingDialog
          open={showCreateDialog}
          onOpenChange={setShowCreateDialog}
          onMeetingCreated={loadMeetings}
          userId={user?.id || ''}
        />
      </div>
    </div>
  );
};

export default MeetingHistoryPage;
